/* eslint-disable lingui/no-unlocalized-strings, lingui/text-restrictions -- Approved First Round marketing copy. */

/* Copy for the First Round marketing page (firstround.com.au). Taken from the supplied reference
   page, with the claims rewritten to what the product actually does. */

export const APP_PATH = "/dashboard/practice-interview";
// Both carry `redirect`: someone arriving from First Round asked to practise, so sign-in lands
// them there rather than in theResume's general dashboard.
export const SIGNUP_PATH = `/auth/register?redirect=${APP_PATH}`;

/**
 * Sign-up (or, signed in, the app) carrying a plan and/or a code through to theResume's plan
 * dialog, which opens with them filled in (?plan=starter&code=MICHAEL on the dashboard).
 */
export const planSignupPath = ({
  plan,
  code,
  signedIn = false,
}: {
  plan?: "free" | "starter" | "active";
  code?: string | null;
  signedIn?: boolean;
}) => {
  const params = new URLSearchParams();
  if (plan) params.set("plan", plan);
  if (code) params.set("code", code);
  const query = params.toString();
  const app = query ? `${APP_PATH}?${query}` : APP_PATH;
  return signedIn ? app : `/auth/register?redirect=${encodeURIComponent(app)}`;
};
export const LOGIN_PATH = `/auth/login?redirect=${APP_PATH}`;
export const SUPPORT_EMAIL = "mailto:support@theresume.io";

// The blog is served by the CMS at firstround.com.au/blog in production only (the load balancer
// routes /blog there), so it is always linked absolutely — theResume has its own /blog page.
export const BLOG_URL = "https://firstround.com.au/blog";

export const nav = [
  { label: "Demo", href: "#demo" },
  { label: "Feedback", href: "#feedback" },
  { label: "Pricing", href: "#pricing" },
  { label: "Blog", href: BLOG_URL },
];

export const hero = {
  titleLead: "The Smartest Way to",
  titleAccent: "Prepare for Your Job Interview",
  lead: "Boost your confidence with realistic AI-powered mock interviews and land your dream job.",
  cta: "Try Live Interview",
  ctaNote: "Free to try, about 2 minutes. No card needed.",
};

/* The hero film since 2026-10-24: "The night before" (growth-engine hype film, variant C, homepage
   edition). Built to be understood muted: every spoken line is on screen as it is said, so it
   autoplays without sound and a click restarts it with sound. Two encodes; the player picks one
   by the width it is shown at. Bump the file version and the id together for a new cut. */
export const nightFilm = {
  id: "first_round_night_v1",
  src720: "/videos/first-round-night-v1-720.mp4",
  src1080: "/videos/first-round-night-v1-1080.mp4",
  poster: "/images/first-round-night-v1-poster.webp",
  // The phone edition (4:5), used at the page's phone breakpoint.
  portrait: {
    src720: "/videos/first-round-night-v1-portrait-720.mp4",
    src1080: "/videos/first-round-night-v1-portrait-1080.mp4",
    poster: "/images/first-round-night-v1-portrait-poster.webp",
  },
  length: "43 seconds",
  label: "The night before your interview: a 43-second film about practising with First Round",
  transcript: [
    "It's the night before your big interview, and you're feeling a bit nervous.",
    "You're reading questions out loud in the mirror, asking your mum and dad, and researching the company. Mum reckons you'll be fine.",
    "All that, and you're still not sure you're ready.",
    "There's a better way with First Round's AI interviewer: choose your role or paste the ad.",
    "Practise out loud with the interviewer, answering questions and follow-ups for your role, then get feedback when you hang up on what to keep and change.",
    "Try a live interview free at firstround.com.au in about two minutes, with no card needed.",
  ],
};

/* The hero film as a Loom playlist (2026-10-16 to 2026-10-24, kept as a fallback): three clips that play one after another, muted, and with sound
   once someone clicks. To swap a clip, paste its Loom share link; titles label the chapter strip.
   Bump the id when the clips change so analytics can tell the cuts apart. */
export const loomFilm = {
  id: "first_round_loom_v1",
  tagline: "Walk in ready.",
  label:
    "A walkthrough of First Round in three short clips: set up the interview, take it, see your feedback",
  clips: [
    {
      title: "Set up the interview",
      url: "https://www.loom.com/share/50ab095378614eb19de781f0bdfb7e34",
    },
    {
      title: "Take the interview",
      url: "https://www.loom.com/share/37f002729357415ab8d02d9a0ded3c08",
    },
    {
      title: "See your feedback",
      url: "https://www.loom.com/share/0dce9ceee0de4e1aae9b2f93ba1aa47f",
    },
  ],
};

export const solution = {
  title: "AI-based practice interviews",
  demoNote:
    "Pick a topic, check your mic, and talk to a live AI interviewer for 2 minutes. Get real feedback on your answers when you sign up.",
};

/* The sample review under the live demo (showcase/Showcase.tsx): five chapters, each running the
   real review components on one fictional interview. Kept short on purpose: the product explains
   itself. `tag` marks what the free demo shows locked. */
export const showcase = {
  eyebrow: "Sample review",
  title: "This is the feedback.",
  lead: "One sample interview, marked up by the real review page.",
  sheetLabel: "Sample",
  sheetMeta: "Graduate analyst · 6 min",
  tryLink: "See yours in 2 minutes",
  cta: "Try it on your own answer",
  chapters: [
    {
      id: "habits",
      title: "Every um, maybe and we.",
      line: "Counted, then found in your own words.",
      tag: "Highlights with a pass",
    },
    {
      id: "keep",
      title: "What to keep. What to change.",
      line: "Your best line, your weakest, and why.",
      tag: null,
    },
    {
      id: "rubric",
      title: "Scored on six things.",
      line: "Against your last session, with what moves each one up.",
      tag: null,
    },
    {
      id: "redo",
      title: "Redo it in 60 seconds.",
      line: "Start from a stronger line. Then see what moved.",
      tag: "With a pass",
    },
    {
      id: "job",
      title: "Checked against the job ad.",
      line: "What you showed, what you missed, and your patterns.",
      tag: "With a pass",
    },
  ],
} as const;

// The plan cards themselves (names, prices, numbers) come from the database through
// PricingCards, the same data as theResume's plan dialog; this is only the section's framing.
export const pricing = {
  eyebrow: "Pricing",
  title: "An Investment in Your Career",
  lead: "Start free. Buy a pass when you have interviews coming up. One-off, no subscription.",
  // What a pass adds over the 2-minute demo, shown as one line above the plans.
  flowLabel: "What a pass gives you",
  flow: [
    "Your role, or paste the job ad",
    "A full interview, with follow-ups",
    "This review, every time",
  ],
};

export const faqs = [
  {
    question: "What exactly is First Round and how does it help me prepare for interviews?",
    answer:
      "First Round is your personal AI interview coach. It conducts realistic practice interviews tailored to your target role, then gives you detailed feedback on your answers and your delivery (pace, filler words, hedging). You leave each session knowing what worked, what to improve, and where to focus next.",
  },
  {
    question: "What types of interviews can I practice for?",
    answer:
      "You can practise in different formats, ranging from friendly and conversational to challenging and rigorous, across a wide variety of topics. Every interview is tailored to your experience and specific circumstances. Paste a role or position description, or add a link to the role, and First Round will create a focused list of questions for that particular opportunity.",
  },
  {
    question: "How is this different from just practicing with a friend?",
    answer:
      "Practising with a friend can be useful, but First Round provides objective, consistent feedback that is difficult for a practice partner to give. It analyses your answer content and delivery, including relevance, structure, pacing, confidence, and filler words, so you can track your progress from one attempt to the next.",
  },
  {
    question: "What do I get for free?",
    answer:
      "Free lets you create, edit, download and tailor resumes to each job, and try a 2-minute practice interview demo with feedback on your answers. No card needed. Full practice interviews for your own jobs, with your video and feedback on every answer, come with a Starter or Active pass.",
  },
  {
    question: "Is it a subscription? Can I cancel?",
    answer:
      "No subscription. Starter and Active are one-off passes, paid once, and nothing renews, so there's nothing to cancel. Your access period starts with your first practice interview, not when you buy, so you can buy ahead of time. A pass you never use lapses 12 months after you buy it.",
  },
  {
    question: "Can I move from Starter to Active?",
    answer:
      "Yes. While you hold a Starter pass you can upgrade to Active and pay only the difference. You keep what you've used, and your access runs for Active's full length from your first practice interview.",
  },
  {
    question: "Is the AI feedback really accurate?",
    answer:
      "Every answer is scored against the same six criteria: relevance, specificity, ownership, result, structure and concision, applied consistently across your practice sessions. While no AI is perfect, the feedback gives you a clear, practical view of your answer quality, communication, and opportunities to improve.",
  },
];

export const finale = {
  eyebrow: "This is the future of hiring.",
  title: "Designed to help you land your dream job.",
  note: "Practice Anywhere, Anytime.",
  cta: "Try Live Interview",
};

export const footer = {
  poweredBy: "Powered by theResume",
  copyright: "© 2026 First Round. All rights reserved.",
  // The legal set (/legal is the hub). Paths resolve per environment in chrome.tsx.
  legal: [
    { label: "Terms of Service", href: "/terms" },
    { label: "Privacy Policy", href: "/privacy" },
    { label: "Trial terms", href: "/trial-terms" },
    { label: "Trial privacy notice", href: "/trial-privacy" },
    { label: "All legal documents", href: "/legal" },
  ],
  columns: [
    {
      title: "Product",
      links: [
        { label: "Demo", href: "#demo" },
        { label: "Feedback", href: "#feedback" },
      ],
    },
    {
      title: "Company",
      links: [
        { label: "About Us", href: "#solution" },
        { label: "Pricing", href: "#pricing" },
        { label: "Blog", href: BLOG_URL },
        { label: "For educational institutes", href: "/for-educational-institutes" },
        { label: "Careers", href: "/careers" },
      ],
    },
    {
      title: "Help & Support",
      links: [
        { label: "Contact Us", href: SUPPORT_EMAIL },
        { label: "Report a Bug", href: SUPPORT_EMAIL },
      ],
    },
  ],
};
